import { CollectionSchema, GameSchema } from '@/server/api/routers';
import { type CollectionMediaType } from '@/types/collection';
import { SupportedMediaType } from '@/types/media';
import { api } from '@/utils/api';
import get from 'lodash.get';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useState } from 'react';

const getFileType = (name: string) => {
  const extension = name.substring(name.lastIndexOf('.') + 1);

  switch (extension) {
    case 'jpeg':
      return SupportedMediaType.JPEG;
    case 'jpg':
      return SupportedMediaType.JPG;
    case 'png':
      return SupportedMediaType.PNG;
    case 'gif':
      return SupportedMediaType.GIF;
    case 'webp':
      return SupportedMediaType.WEBP;
    default:
      return undefined;
  }
};

export enum MediaUploadType {
  COLLECTION = 'collection',
  GAME = 'game',
}

type ValidateMediaParams =
  | {
      type: MediaUploadType.COLLECTION;
      file: File;
      chainId: number;
      address: string;
      srcPath: string;
      mediaType: CollectionMediaType;
      ogPath?: string;
    }
  | {
      type: MediaUploadType.GAME;
      file: File;
      chainId: number;
      slug: string;
      srcPath: string;
      mediaType: CollectionMediaType;
      ogPath?: string;
    };

export const useMediaUpload = () => {
  const { t } = useTranslation('editor');
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const getUploadUrl = api.media.getUploadUrl.useMutation();
  const validateCollectionMedia = api.media.validateCollectionMedia.useMutation();
  const validateGameMedia = api.media.validateGameMedia.useMutation();

  const uploadMedia = useCallback(
    (params: ValidateMediaParams): Promise<{ src: string; og?: string }> =>
      new Promise(async (resolve, reject) => {
        setLoading(true);
        setError(undefined);

        const { file, chainId, srcPath, mediaType, ogPath } = params;

        const fileType = getFileType(file.name);

        try {
          if (!fileType) {
            throw new Error(t('components.media-upload.errors.file-type'));
          }

          const uploadUrl = await getUploadUrl.mutateAsync({ fileType });

          if (!uploadUrl.url) {
            throw new Error(t('components.media-upload.errors.upload-url'));
          }

          await fetch(uploadUrl.url, {
            method: 'PUT',
            body: file,
          });

          let updated: GameSchema | CollectionSchema | null = null;

          if (params.type === MediaUploadType.COLLECTION) {
            const updatedCollection = await validateCollectionMedia.mutateAsync({
              key: uploadUrl.key,
              chainId: params.chainId,
              address: params.address,
              srcPath,
              ogPath,
              mediaType,
            });

            updated = updatedCollection;
          }

          if (params.type === MediaUploadType.GAME) {
            const updatedGame = await validateGameMedia.mutateAsync({
              key: uploadUrl.key,
              chainId,
              slug: params.slug,
              srcPath,
              ogPath,
              mediaType,
            });

            updated = updatedGame;
          }

          if (!updated) throw new Error(t('components.media-upload.errors.generic'));

          setLoading(false);
          resolve({
            src: get(updated, srcPath),
            og: ogPath ? get(updated, ogPath) : undefined,
          });
        } catch (error) {
          setLoading(false);
          setError(String(error || ''));
          reject(error || t('components.media-upload.errors.generic'));
        }
      }),
    [getUploadUrl, validateCollectionMedia, validateGameMedia, t],
  );

  return { uploadMedia, isLoading, error };
};
